import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { t } from "@/lib/i18n";

const STUDIO_WHATSAPP = "905428786499";
const WA_TITLE = { tr: "Yeni Teklif Talebi", en: "New Quote Request", el: "Νέο Αίτημα Προσφοράς", ru: "Новая заявка", fa: "درخواست قیمت جدید" };

export default function LeadForm({ defaultService = "", defaultBranch = "" }) {
  const { lang } = useParams();
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", whatsapp: "", email: "", vehicle_brand: "", vehicle_model: "",
    vehicle_year: "", service: defaultService, branch: defaultBranch, message: "", hp: "",
  });

  const { data: nav } = useQuery({
    queryKey: ["nav", lang],
    queryFn: async () => (await api.get(`/public/nav/${lang}`)).data,
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const utm = () => {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || "", utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "", referrer: document.referrer || "",
      source_page: window.location.pathname,
    };
  };

  const buildWaMessage = () => {
    const lines = [
      `*${WA_TITLE[lang] || WA_TITLE.en} — Sıfır Bir Tuning*`,
      `${t(lang, "name")}: ${form.name}`,
      `${t(lang, "phone")}: ${form.phone}`,
    ];
    if (form.whatsapp) lines.push(`${t(lang, "whatsapp")}: ${form.whatsapp}`);
    if (form.email) lines.push(`${t(lang, "email")}: ${form.email}`);
    const vehicle = [form.vehicle_brand, form.vehicle_model].filter(Boolean).join(" ");
    if (vehicle) lines.push(`${t(lang, "brand")}: ${vehicle}`);
    if (form.service) lines.push(`${t(lang, "service")}: ${form.service}`);
    if (form.branch) lines.push(`${t(lang, "branch")}: ${form.branch}`);
    if (form.message) lines.push(`${t(lang, "message")}: ${form.message}`);
    return lines.join("\n");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const wa = window.open("", "_blank");
    try {
      await api.post("/leads", { ...form, language: lang, ...utm() });
      setDone(true);
      toast.success(t(lang, "successMsg"));
      const url = `https://wa.me/${STUDIO_WHATSAPP}?text=${encodeURIComponent(buildWaMessage())}`;
      if (wa) wa.location.href = url; else window.open(url, "_blank", "noopener");
    } catch (err) {
      if (wa) wa.close();
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-form-success" className="card-surface p-8 text-center">
        <CheckCircle2 className="mx-auto text-copper mb-4" size={48} />
        <p className="text-ink text-lg">{t(lang, "successMsg")}</p>
      </div>
    );
  }

  const input = "w-full bg-obsidian border border-white/15 rounded-lg px-4 py-3 text-sm text-ink placeholder:text-faint focus:border-copper transition-colors rtl:text-right";

  return (
    <form data-testid="lead-form" onSubmit={submit} className="card-surface p-6 sm:p-8 space-y-4">
      <input type="text" name="hp" value={form.hp} onChange={set("hp")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-subtle mb-1.5">{t(lang, "name")} *</label>
          <input data-testid="lead-name" required value={form.name} onChange={set("name")} className={input} />
        </div>
        <div>
          <label className="block text-xs text-subtle mb-1.5">{t(lang, "phone")} *</label>
          <input data-testid="lead-phone" required value={form.phone} onChange={set("phone")} className={input} />
        </div>
        <div>
          <label className="block text-xs text-subtle mb-1.5">{t(lang, "whatsapp")}</label>
          <input data-testid="lead-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} className={input} />
        </div>
        <div>
          <label className="block text-xs text-subtle mb-1.5">{t(lang, "email")}</label>
          <input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} className={input} />
        </div>
        <div>
          <label className="block text-xs text-subtle mb-1.5">{t(lang, "brand")}</label>
          <input data-testid="lead-brand" value={form.vehicle_brand} onChange={set("vehicle_brand")} className={input} />
        </div>
        <div>
          <label className="block text-xs text-subtle mb-1.5">{t(lang, "model")}</label>
          <input data-testid="lead-model" value={form.vehicle_model} onChange={set("vehicle_model")} className={input} />
        </div>
        <div>
          <label className="block text-xs text-subtle mb-1.5">{t(lang, "service")}</label>
          <select data-testid="lead-service" value={form.service} onChange={set("service")} className={input}>
            <option value="">{t(lang, "selectService")}</option>
            {(nav?.services || []).map((s) => <option key={s.entity_id} value={s.title}>{s.title}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-subtle mb-1.5">{t(lang, "branch")}</label>
          <select data-testid="lead-branch" value={form.branch} onChange={set("branch")} className={input}>
            <option value="">{t(lang, "selectBranch")}</option>
            {(nav?.branches || []).map((b) => <option key={b.entity_id} value={b.title}>{b.title}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs text-subtle mb-1.5">{t(lang, "message")}</label>
        <textarea data-testid="lead-message" rows={3} value={form.message} onChange={set("message")} className={input} />
      </div>
      <button data-testid="lead-submit" type="submit" disabled={sending} className="btn-copper w-full disabled:opacity-60">
        {sending ? t(lang, "sending") : t(lang, "send")}
      </button>
    </form>
  );
}
